import type { AttachmentData, AttachmentMessageBlock } from "@/lib/chat-types";
import type { JsonRecord } from "@/lib/fund-compliance/parse-primitives";
import { resolveString, toRecord } from "@/lib/fund-compliance/parse-primitives";

/* ── Field resolution keys ───────────────────────────────────────── */

/** v1 API: `filename`, v2 API: `name` */
const FILENAME_KEYS = ["filename", "name"] as const;
/** v1 API: `mimeType`, v2 API: `type` */
const MIMETYPE_KEYS = ["mimeType", "type"] as const;
/** v1 API: `filesize`, v2 API: `sizeLabel` */
const FILESIZE_KEYS = ["filesize", "sizeLabel"] as const;

/* ── Helpers ─────────────────────────────────────────────────────── */

function mimeToFiletype(mimeType?: string): string {
  if (!mimeType) return "file";
  const slashIndex = mimeType.indexOf("/");
  if (slashIndex === -1) return mimeType.toUpperCase();
  return mimeType.slice(slashIndex + 1).toUpperCase();
}

function createAttachmentBlock(record: JsonRecord): AttachmentMessageBlock | null {
  const filename = resolveString(record, FILENAME_KEYS);
  if (!filename) return null;

  const mimeType = resolveString(record, MIMETYPE_KEYS);

  const attachment: AttachmentData = {
    filename,
    filesize: resolveString(record, FILESIZE_KEYS) ?? "Uploaded file",
    filetype: mimeToFiletype(mimeType),
    status: "uploaded",
  };

  return { type: "attachment", attachment };
}

function extractAttachmentList(value: unknown): AttachmentMessageBlock[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const record = toRecord(item);
      return record ? createAttachmentBlock(record) : null;
    })
    .filter((block): block is AttachmentMessageBlock => block !== null);
}

/* ── Public API ──────────────────────────────────────────────────── */

/**
 * Extract all attachment blocks from a message record.
 * Looks in `record.attachments`, `record.metadata.attachments`,
 * `record.attachment`, and `record.metadata.attachment`.
 * Deduplicates by filename.
 */
export function extractAllAttachmentBlocks(record: JsonRecord): AttachmentMessageBlock[] {
  const metadata = toRecord(record.metadata);
  const blocks = [
    ...extractAttachmentList(record.attachments),
    ...extractAttachmentList(metadata?.attachments),
  ];

  const singleAttachment =
    createAttachmentBlock(toRecord(record.attachment) ?? {}) ??
    createAttachmentBlock(toRecord(metadata?.attachment) ?? {});

  if (singleAttachment) {
    blocks.push(singleAttachment);
  }

  const deduped = new Map<string, AttachmentMessageBlock>();
  for (const block of blocks) {
    deduped.set(block.attachment.filename, block);
  }

  return [...deduped.values()];
}
